import { Link, useNavigate } from "react-router-dom";
import {
  FileCheck2, Building2, User as UserIcon, Shield,
  ArrowRight, CheckCircle2, Calculator, FileSignature, Mail,
} from "lucide-react";

/**
 * Public landing page for Unioncasa Milano — Attestazione Canone Concordato.
 *
 * Identity: navy (#0f2847 / #1e3a5f) + white, serif institutional typography,
 * no yellow accents. Visual language inspired by unioncasamilano.com.
 *
 * CTAs:
 *   - Primary → /attestazione (12-step form)
 *   - Secondary → /agenzia/login, /utente/login
 */
export default function LandingPage() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* TOP BAR */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
          <img
            src="/logo-unioncasa-white.png"
            alt="Unioncasa — Associazione Nazionale a tutela della proprietà immobiliare"
            className="h-16 lg:h-20 w-auto select-none"
            style={{ filter: "invert(1) brightness(0.1) saturate(1.2) hue-rotate(190deg)" }}
            data-testid="landing-logo"
          />
          <nav className="flex items-center gap-2 text-xs">
            <Link
              to="/utente/login"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[#0f2847] border border-[#0f2847]/20 hover:border-[#0f2847] hover:bg-[#0f2847]/5 font-medium transition-colors"
              data-testid="header-login-locatore"
            >
              <UserIcon size={13} /> Login Locatore
            </Link>
            <Link
              to="/agenzia/login"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[#0f2847] border border-[#0f2847]/20 hover:border-[#0f2847] hover:bg-[#0f2847]/5 font-medium transition-colors"
              data-testid="header-login-agenzia"
            >
              <Building2 size={13} /> Login Professionista incaricato
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f6f8fc] via-white to-[#eef2f9]">
        {/* subtle decorative lines evoking an institutional seal */}
        <div
          className="absolute inset-y-0 right-0 w-1/2 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg, #0f2847 0, #0f2847 1px, transparent 1px, transparent 20px)",
          }}
        />
        <div className="absolute top-0 left-0 w-full h-1 bg-[#0f2847]" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#1e3a5f] font-semibold flex items-center gap-2">
              <span className="w-10 h-px bg-[#1e3a5f]" />
              Attestazione · Canone Concordato
            </p>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-[3.75rem] font-light leading-[1.1] mt-5 text-[#0f2847]"
              data-testid="landing-hero-title"
            >
              La tua attestazione <br />
              per il <span className="italic">Canone concordato</span>
              <br />
              a <span className="font-normal">Milano</span>, in 12 step.
            </h1>
            <div className="h-[2px] w-20 bg-[#0f2847] mt-6" />
            <p className="text-base lg:text-lg text-slate-700 mt-6 leading-relaxed max-w-xl">
              Unioncasa Milano cura la{" "}
              <strong className="text-[#0f2847]">
                rispondenza del contratto all&apos;Accordo territoriale del 12/06/2023
              </strong>
              . Compila il modulo online: ti guidiamo passo per passo,
              calcoliamo i canoni e generiamo il contratto pronto da firmare.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                type="button"
                onClick={() => nav("/attestazione")}
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-[#0f2847] text-white text-sm font-semibold uppercase tracking-[0.08em] hover:bg-[#1a3860] transition-colors shadow-lg shadow-[#0f2847]/15"
                data-testid="landing-cta-start"
              >
                <FileCheck2 size={16} /> Avvia richiesta di attestazione canone concordato
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/agenzia/login"
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md border border-[#0f2847]/25 text-sm text-[#0f2847] font-medium hover:border-[#0f2847] hover:bg-[#0f2847]/5 transition-colors"
                data-testid="landing-cta-agenzia"
              >
                <Building2 size={14} /> Login Professionista incaricato
              </Link>
              <Link
                to="/utente/login"
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md border border-[#0f2847]/25 text-sm text-[#0f2847] font-medium hover:border-[#0f2847] hover:bg-[#0f2847]/5 transition-colors"
                data-testid="landing-cta-locatore"
              >
                <UserIcon size={14} /> Login Locatore
              </Link>
            </div>

            <p className="text-[11px] text-slate-500 mt-6 leading-relaxed max-w-md">
              Nessun account richiesto per iniziare. I dati sono salvati
              in locale sul tuo dispositivo come bozza finché non invii la pratica.
            </p>
          </div>

          {/* Hero visual: institutional info card */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#0f2847]/15 rounded-sm shadow-xl shadow-[#0f2847]/10 p-6 lg:p-7 relative">
              <div className="absolute -top-px left-6 right-6 h-[3px] bg-[#0f2847]" />
              <div className="flex items-center gap-2 text-[#0f2847] text-[10px] uppercase tracking-[0.25em] font-semibold mb-5">
                <Shield size={13} /> Pratica certificata
              </div>
              <ul className="space-y-4">
                {[
                  { ic: Calculator, t: "Calcolo canone", d: "automatizzato in base ai 25 criteri" },
                  { ic: FileSignature, t: "Contratto generato", d: "pronto da firmare dalle parti" },
                  { ic: Mail, t: "Notifiche", d: "a locatore, conduttore e professionista" },
                  { ic: CheckCircle2, t: "Attestazione firmata", d: "scaricabile dal pannello" },
                ].map(({ ic: Ic, t, d }, i) => (
                  <li key={i} className="flex items-start gap-3 border-b border-slate-100 last:border-b-0 pb-3 last:pb-0">
                    <span className="w-9 h-9 rounded-sm bg-[#0f2847]/5 text-[#0f2847] flex items-center justify-center shrink-0 mt-0.5 border border-[#0f2847]/10">
                      <Ic size={16} />
                    </span>
                    <div>
                      <p className="font-medium text-[#0f2847] text-[13px]">{t}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INSTITUTIONAL CREDENTIAL STRIP */}
      <section className="bg-[#0f2847] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 grid sm:grid-cols-3 gap-6 text-center sm:text-left">
          {[
            { k: "1999", v: "Associazione maggiormente rappresentativa di categoria" },
            { k: "D.M. 2017", v: "Riconoscimento Ministero Infrastrutture e Trasporti" },
            { k: "L. 431/98", v: "Firmataria degli Accordi locali Canoni Concordati" },
          ].map((x, i) => (
            <div key={i} className="flex items-start gap-3 justify-center sm:justify-start">
              <div className="w-px bg-white/30 self-stretch hidden sm:block" />
              <div>
                <p className="font-serif text-xl text-white">{x.k}</p>
                <p className="text-xs text-slate-300 mt-1 leading-relaxed">{x.v}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURE STRIP — role cards */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#1e3a5f] font-semibold flex items-center gap-2">
          <span className="w-10 h-px bg-[#1e3a5f]" />
          Cosa fa per te Unioncasa
        </p>
        <h2 className="font-serif text-3xl lg:text-4xl text-[#0f2847] mt-4 max-w-2xl leading-tight font-light">
          Un unico modulo. <span className="italic">Tre percorsi</span>, a seconda del tuo ruolo.
        </h2>
        <div className="h-[2px] w-16 bg-[#0f2847] mt-5" />

        <div className="grid md:grid-cols-3 gap-5 mt-12">
          <RoleCard
            testid="role-locatore"
            icon={<UserIcon size={16} />}
            label="Sei il locatore"
            title="Compila la tua pratica"
            desc="Avvia una nuova richiesta: ti bastano visura catastale, APE e dati del conduttore. Riceverai email a ogni passaggio."
            cta="Avvia richiesta →"
            onClick={() => nav("/attestazione")}
            primary
          />
          <RoleCard
            testid="role-agenzia"
            icon={<Building2 size={16} />}
            label="Sei un professionista incaricato"
            title="Gestisci le pratiche dei tuoi clienti"
            desc="Registra il tuo profilo una sola volta: tutte le pratiche compilate per i locatori appaiono nel tuo pannello dedicato."
            cta="Accedi come Professionista →"
            to="/agenzia/login"
          />
          <RoleCard
            testid="role-locatore-login"
            icon={<FileCheck2 size={16} />}
            label="Hai già una pratica aperta"
            title="Accedi al tuo pannello"
            desc="Monitora lo stato della pratica, ricarica eventuali documenti richiesti e firma il contratto quando autorizzato dall'admin."
            cta="Login Locatore →"
            to="/utente/login"
          />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-[#f6f8fc]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-500">
          <p>D.M. 16/01/2017 — Attestazione di rispondenza di contratto a canone concordato.</p>
          <p>Accordo territoriale per Milano del 12/06/2023.</p>
        </div>
      </footer>
    </div>
  );
}

function RoleCard({ testid, icon, label, title, desc, cta, onClick, to, primary = false }) {
  const Btn = (props) => (onClick
    ? <button type="button" onClick={onClick} {...props} />
    : <Link to={to} {...props} />
  );
  return (
    <div
      className={`rounded-sm border flex flex-col p-6 lg:p-7 transition-colors ${
        primary
          ? "bg-[#0f2847] text-white border-[#0f2847]"
          : "bg-white text-[#0f2847] border-[#0f2847]/15 hover:border-[#0f2847]/40"
      }`}
      data-testid={testid}
    >
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold ${primary ? "text-slate-300" : "text-slate-500"}`}>
        <span className={`w-6 h-6 rounded-sm flex items-center justify-center ${primary ? "bg-white/10 text-white" : "bg-[#0f2847]/5 text-[#0f2847]"}`}>
          {icon}
        </span>
        {label}
      </div>
      <h3 className={`font-serif text-xl mt-4 leading-snug font-light ${primary ? "text-white" : "text-[#0f2847]"}`}>{title}</h3>
      <p className={`text-sm mt-3 flex-1 leading-relaxed ${primary ? "text-slate-300" : "text-slate-600"}`}>{desc}</p>
      <Btn
        className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium mt-5 self-start ${
          primary
            ? "bg-white text-[#0f2847] hover:bg-slate-100"
            : "bg-[#0f2847] text-white hover:bg-[#1a3860]"
        }`}
        data-testid={`${testid}-cta`}
      >
        {cta}
      </Btn>
    </div>
  );
}
